import { useId } from 'react';
import { useGT } from 'gt-react';
import { cn } from '@/lib/utils';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import { isAdjustScope, type AdjustScope } from '../utils/revisedCostAdjust';

export function CostAdjustmentScope({
  scope,
  onChange,
  selectedCount,
  orderBuyCount,
}: {
  scope: AdjustScope;
  onChange: (scope: AdjustScope) => void;
  selectedCount: number;
  orderBuyCount: number;
}) {
  const gt = useGT();
  const idPrefix = useId();
  // A selection of one line is the line itself, so "Selected lines" waits
  // for a shift-click highlight of two or more.
  const hasSelection = selectedCount > 1;
  const options: { value: AdjustScope; label: string; disabled?: boolean }[] = [
    {
      value: 'line',
      label: gt('This line', {
        context: 'revised cost adjust scope option',
      }),
    },
    {
      value: 'selected',
      label: hasSelection
        ? gt('{n} selected lines', {
            context: 'revised cost adjust scope option',
            n: selectedCount,
          })
        : gt('Selected lines (shift-click rows to select)', {
            context:
              'revised cost adjust scope option, disabled until rows are shift-click highlighted',
          }),
      disabled: !hasSelection,
    },
    {
      value: 'order',
      label: gt('Whole order ({n} lines)', {
        context: 'revised cost adjust scope option: every buy line',
        n: orderBuyCount,
      }),
    },
  ];
  return (
    <RadioGroup
      value={scope}
      onValueChange={(value) => {
        if (isAdjustScope(value)) onChange(value);
      }}
      aria-label={gt('Apply to', {
        context: 'revised cost adjust scope picker label',
      })}
      className="flex flex-col gap-1"
    >
      {options.map((option) => (
        <label
          key={option.value}
          htmlFor={`${idPrefix}-${option.value}`}
          className={cn(
            'flex items-center gap-2 text-xs',
            option.disabled
              ? 'cursor-not-allowed text-muted-foreground'
              : 'cursor-pointer',
          )}
        >
          <RadioGroupItem
            value={option.value}
            id={`${idPrefix}-${option.value}`}
            disabled={option.disabled}
          />
          {option.label}
        </label>
      ))}
    </RadioGroup>
  );
}
